import { ChevronLeft, Play, Pause, Square, Mic } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

type Topic = {
  id: string;
  title: string;
  description: string;
};

type Language = {
  id: string;
  name: string;
  icon: string;
  color: string;
  topics: Topic[];
};

type Question = {
  id: number;
  text: string;
  audioText: string;
};

type WaveformProps = {
  isRecording: boolean;
};

function Waveform({ isRecording }: WaveformProps) {
  // Mock waveform data - in production this would be real audio analysis
  const bars = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    height: isRecording ? Math.random() * 60 + 10 : 20
  }));

  return (
    <div className='flex h-24 items-center justify-center rounded-lg bg-slate-50 p-4'>
      <div className='flex h-16 items-end space-x-1'>
        {bars.map(bar => (
          <div
            key={bar.id}
            className={`waveform-bar w-2 rounded-full transition-all duration-200 ${
              isRecording ? 'bg-red-500' : 'bg-slate-300'
            }`}
            style={{ height: `${bar.height}%` }}
          />
        ))}
      </div>
    </div>
  );
}

const AiTest = ({
  setCurrentView,
  currentView
}: {
  setCurrentView: (
    val: 'setup' | 'interview' | 'complete' | 'home' | 'written'
  ) => void;
  currentView: string;
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('java');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('');

  // Interview state
  const [currentQuestion, setCurrentQuestion] = useState<number>(1);
  const [totalTime, setTotalTime] = useState<number>(30 * 60); // 30 minutes in seconds
  const [questionTime, setQuestionTime] = useState<number>(2 * 60); // 2 minutes in seconds
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioWaves, setAudioWaves] = useState<number[]>(Array(15).fill(4));
  const [isQuestionPlaying, setIsQuestionPlaying] = useState<boolean>(false);
  const [questionProgress, setQuestionProgress] = useState<number>(0);
  const [transcript, setTranscript] = useState<string>('');
  const [canStartAnswering, setCanStartAnswering] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const speechSynthesisRef = useRef<SpeechSynthesisUtterance | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const questionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const waveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const disableStartInterview = () => {
    return (
      !selectedLanguage || selectedTopics.length === 0 || !selectedDifficulty
    );
  };

  const languages: Language[] = [
    {
      id: 'java',
      name: 'Java',
      icon: '☕',
      color: 'bg-red-100',
      topics: [
        {
          id: 'oop',
          title: 'Object-Oriented Programming',
          description: 'Classes, Objects, Inheritance, Polymorphism'
        },
        {
          id: 'ds',
          title: 'Data Structures',
          description: 'Arrays, Lists, Trees, Graphs'
        },
        {
          id: 'algorithms',
          title: 'Algorithms',
          description: 'Sorting, Searching, Dynamic Programming'
        },
        {
          id: 'frameworks',
          title: 'Frameworks & Libraries',
          description: 'Popular frameworks and tools'
        }
      ]
    },
    {
      id: 'python',
      name: 'Python',
      icon: '🐍',
      color: 'bg-green-100',
      topics: [
        {
          id: 'oop',
          title: 'Object-Oriented Programming',
          description: 'Classes, Objects, Inheritance, Polymorphism'
        }
      ]
    },
    {
      id: 'javascript',
      name: 'JavaScript',
      icon: '⚡',
      color: 'bg-yellow-100',
      topics: [
        {
          id: 'ds',
          title: 'Data Structures',
          description: 'Arrays, Lists, Trees, Graphs'
        }
      ]
    },
    { id: 'cpp', name: 'C++', icon: '⚡', color: 'bg-orange-100', topics: [] },
    { id: 'csharp', name: 'C#', icon: '#', color: 'bg-blue-100', topics: [] }
  ];

  const topics: Topic[] = [
    {
      id: 'oop',
      title: 'Object-Oriented Programming',
      description: 'Classes, Objects, Inheritance, Polymorphism'
    },
    {
      id: 'ds',
      title: 'Data Structures',
      description: 'Arrays, Lists, Trees, Graphs'
    },
    {
      id: 'algorithms',
      title: 'Algorithms',
      description: 'Sorting, Searching, Dynamic Programming'
    },
    {
      id: 'frameworks',
      title: 'Frameworks & Libraries',
      description: 'Popular frameworks and tools'
    }
  ];

  const difficulties = [
    {
      id: 'beginner',
      label: 'Beginner',
      description: 'Basic concepts and fundamentals',
      color: 'text-green-600'
    },
    {
      id: 'intermediate',
      label: 'Intermediate',
      description: 'Moderate complexity problems',
      color: 'text-yellow-600'
    },
    {
      id: 'advanced',
      label: 'Advanced',
      description: 'Complex scenarios and optimization',
      color: 'text-red-600'
    }
  ];

  const questions: Question[] = [
    {
      id: 1,
      text: 'What is a class in Java? Can you explain the basic structure of a class with an example?',
      audioText:
        'What is a class in Java? Can you explain the basic structure of a class with an example?'
    },
    {
      id: 2,
      text: 'Explain the concept of inheritance in Java. How does it promote code reusability?',
      audioText:
        'Explain the concept of inheritance in Java. How does it promote code reusability?'
    },
    {
      id: 3,
      text: 'What is the difference between method overloading and method overriding in Java?',
      audioText:
        'What is the difference between method overloading and method overriding in Java?'
    },
    {
      id: 4,
      text: 'How does exception handling work in Java? Explain try-catch-finally blocks.',
      audioText:
        'How does exception handling work in Java? Explain try-catch-finally blocks.'
    },
    {
      id: 5,
      text: 'What are the key features of Java that make it platform independent?',
      audioText:
        'What are the key features of Java that make it platform independent?'
    }
  ];

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTopicToggle = (topicId: string) => {
    setSelectedTopics(prev =>
      prev.includes(topicId)
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId]
    );
  };

  const startInterview = () => {
    setCurrentView('interview');
    startTotalTimer();
    playQuestionAudio();
  };

  const startTotalTimer = () => {
    timerRef.current = setInterval(() => {
      setTotalTime(prev => {
        if (prev <= 1) {
          completeInterview();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startQuestionTimer = () => {
    setQuestionTime(2 * 60);
    questionTimerRef.current = setInterval(() => {
      setQuestionTime(prev => {
        if (prev <= 1) {
          // Auto submit when time runs out
          handleSubmitAnswer();
          return 2 * 60;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const playQuestionAudio = () => {
    if ('speechSynthesis' in window) {
      // Stop any ongoing speech
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(
        questions[currentQuestion - 1]?.audioText
      );
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;

      speechSynthesisRef.current = utterance;

      setIsQuestionPlaying(true);
      setQuestionProgress(0);
      setCanStartAnswering(false);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setQuestionProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 2;
        });
      }, 100);

      utterance.onend = () => {
        setIsQuestionPlaying(false);
        setCanStartAnswering(true);
        startQuestionTimer();
        clearInterval(progressInterval);
      };

      utterance.onerror = () => {
        setIsQuestionPlaying(false);
        setCanStartAnswering(true);
        startQuestionTimer();
        clearInterval(progressInterval);
      };

      window.speechSynthesis.speak(utterance);
    } else {
      // Fallback if speech synthesis not supported
      setIsQuestionPlaying(true);
      setTimeout(() => {
        setIsQuestionPlaying(false);
        setCanStartAnswering(true);
        startQuestionTimer();
      }, 5000);
    }
  };

  const toggleQuestionAudio = () => {
    if (isQuestionPlaying) {
      window.speechSynthesis.pause();
    } else if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    } else {
      playQuestionAudio();
    }
  };

  const setupAudioAnalyser = (stream: MediaStream) => {
    try {
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);

      analyserRef.current.fftSize = 64;
      analyserRef.current.smoothingTimeConstant = 0.8;
      source.connect(analyserRef.current);

      animateWaves();
    } catch (error) {
      console.error('Error setting up audio analyser:', error);
      // Fallback to basic animation
      startBasicWaveAnimation();
    }
  };

  const animateWaves = () => {
    if (!analyserRef.current || !isRecording) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const animate = () => {
      if (!isRecording || !analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);

      // Convert frequency data to wave heights
      const newWaves = Array(15)
        .fill(0)
        .map((_, i) => {
          const binIndex = Math.floor((i / 15) * bufferLength);
          const value = dataArray[binIndex] || 0;
          // Scale the value to a reasonable height (4-50px)
          return Math.max(4, (value / 255) * 46 + 4);
        });

      setAudioWaves(newWaves);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
  };

  const startBasicWaveAnimation = () => {
    waveIntervalRef.current = setInterval(() => {
      if (isRecording) {
        setAudioWaves(prev => prev.map(() => Math.random() * 46 + 4));
      }
    }, 100);
  };

  const stopWaveAnimation = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (waveIntervalRef.current) {
      clearInterval(waveIntervalRef.current);
      waveIntervalRef.current = null;
    }
    setAudioWaves(Array(15).fill(4));
  };

  const startRecording = async () => {
    if (!canStartAnswering) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = event => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);

      // Setup audio visualization
      setupAudioAnalyser(stream);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      // Stop wave animation
      stopWaveAnimation();

      // Close audio context
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== 'closed'
      ) {
        audioContextRef.current.close();
      }

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/wav'
        });
        // Here you would typically send the audio to a transcription service
        setTranscript('Your answer has been recorded successfully.');
      };

      // Stop all tracks
      const stream = mediaRecorderRef.current.stream;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const handleSubmitAnswer = () => {
    if (isSubmitting) return;

    stopRecording();
    setIsSubmitting(true);

    // Clear question timer
    if (questionTimerRef.current) {
      clearInterval(questionTimerRef.current);
    }

    setTimeout(() => {
      setIsSubmitting(false);
      if (currentQuestion < 5) {
        setCurrentQuestion(prev => prev + 1);
        setTranscript('');
        setCanStartAnswering(false);
        playQuestionAudio();
      } else {
        completeInterview();
      }
    }, 2000);
  };

  const completeInterview = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (questionTimerRef.current) clearInterval(questionTimerRef.current);
    stopWaveAnimation();
    window.speechSynthesis.cancel();
    stopRecording();
    setCurrentView('complete');
  };

  const goToPrevious = () => {
    if (currentQuestion > 1 && !isSubmitting) {
      if (questionTimerRef.current) clearInterval(questionTimerRef.current);
      stopRecording();
      setCurrentQuestion(prev => prev - 1);
      setTranscript('');
      setCanStartAnswering(false);
      playQuestionAudio();
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (questionTimerRef.current) clearInterval(questionTimerRef.current);
      stopWaveAnimation();
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== 'closed'
      ) {
        audioContextRef.current.close();
      }
      window.speechSynthesis.cancel();
    };
  }, []);

  if (currentView === 'complete') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gray-50'>
        <div className='mx-4 w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg'>
          <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100'>
            <svg
              className='h-8 w-8 text-green-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
          <h2 className='mb-2 text-2xl font-bold text-gray-800'>
            Interview Complete!
          </h2>
          <p className='mb-6 text-gray-600'>
            Thank you for completing the Java interview. Your responses have
            been recorded and will be reviewed.
          </p>
          <button
            onClick={() => {
              setCurrentView('setup');
              setCurrentQuestion(1);
              setTotalTime(30 * 60);
              setQuestionTime(2 * 60);
              setTranscript('');
              setCanStartAnswering(false);
            }}
            className='rounded-lg bg-blue-500 px-6 py-2 font-medium text-white transition-colors hover:bg-blue-600'
          >
            View Test Results
          </button>
        </div>
      </div>
    );
  }

  if (currentView === 'interview') {
    return (
      <div className='mb-10 h-full overflow-y-auto bg-gray-50 p-4'>
        <div className='mx-auto h-full max-w-4xl'>
          {/* Header */}
          <div className='mb-6 flex items-center justify-between'>
            <div className='flex-1 text-center'>
              <h1 className='text-xl font-semibold text-gray-800'>
                Java Interview
              </h1>
              <p className='text-sm text-gray-500'>
                Question {currentQuestion} of 5
              </p>
            </div>
            <div className='text-right'>
              <div className='font-mono text-lg text-red-500'>
                {formatTime(totalTime)}
              </div>
              <div className='text-xs text-gray-500'>Time Remaining</div>
            </div>
          </div>

          {/* Question Audio */}
          <div className='mb-6 rounded-lg  border border-gray-200  bg-white p-6 shadow-sm'>
            <h3 className='mb-4 text-sm font-medium text-gray-700'>
              Question Audio
            </h3>
            <div className='flex items-center space-x-4 rounded-lg bg-[#F8FAFC] p-5'>
              <button
                onClick={toggleQuestionAudio}
                className='flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 text-white transition-colors hover:bg-blue-600'
              >
                {isQuestionPlaying ? (
                  <Pause className='h-5 w-5' />
                ) : (
                  <Play className='h-5 w-5' />
                )}
              </button>
              <div className='flex-1'>
                <div className='h-2 w-full rounded-full bg-gray-200'>
                  <div
                    className='h-2 rounded-full bg-blue-500 transition-all duration-300'
                    style={{ width: `${questionProgress}%` }}
                  ></div>
                </div>
                <div className='mt-1 flex justify-between text-xs text-gray-500'>
                  <span>0:00</span>
                  <span>0:10</span>
                </div>
              </div>
            </div>

            {/* Question Text */}
            <div className='mt-6 rounded-lg border border-gray-200 p-5'>
              <h4 className='mb-2 text-sm font-medium text-gray-700'>
                Question Text
              </h4>
              <p className='text-gray-600'>
                {questions[currentQuestion - 1]?.text}
              </p>
            </div>
          </div>

          {/* Response Section */}
          <div className='mb-6 rounded-lg bg-white p-6 shadow-sm'>
            <h3 className='mb-4 text-sm font-medium text-gray-700'>
              Your Response
            </h3>

            {/* Recording Controls */}
            <div className='mb-6 flex items-center justify-center space-x-4'>
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={!canStartAnswering}
                className={`flex h-12 w-12 items-center justify-center rounded-full text-white transition-colors ${
                  !canStartAnswering
                    ? 'cursor-not-allowed bg-gray-400'
                    : isRecording
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {isRecording ? (
                  <Square className='h-6 w-6' />
                ) : (
                  <Mic className='h-6 w-6' />
                )}
              </button>
              <button
                className='flex h-12 w-12 cursor-not-allowed items-center justify-center rounded-full bg-gray-400 text-white'
                disabled
              >
                <Pause className='h-6 w-6' />
              </button>
              <button
                className='flex h-12 w-12 items-center justify-center rounded-full bg-green-500 text-white transition-colors hover:bg-green-600'
                disabled={!canStartAnswering}
              >
                <Play className='h-6 w-6' />
              </button>
            </div>

            {/* Recording Status */}
            <div className='mb-4 text-center'>
              <div className='text-sm text-gray-600'>
                {!canStartAnswering
                  ? 'Listen to the question first...'
                  : isRecording
                    ? 'Recording...'
                    : 'Ready to record'}
              </div>
              {canStartAnswering && (
                <div className='text-xs text-gray-400'>
                  {formatTime(questionTime)}
                </div>
              )}
            </div>

            {/* Audio Visualization */}
            {/* <div className='mb-6 flex h-16 items-end justify-center space-x-1 rounded-lg bg-[#F8FAFC] pb-4'> */}
            <Waveform isRecording={isRecording} />
            {/* </div> */}
          </div>

          {/* Answer Transcript */}
          <div className='mb-6 rounded-lg bg-white p-6 shadow-sm'>
            <h3 className='mb-4 text-sm font-medium text-gray-700'>
              Answer Transcript
            </h3>
            <div className='rounded-lg bg-[#F8FAFC] p-6 text-sm italic text-gray-500'>
              {transcript ||
                'Transcript will appear here after you submit your answer...'}
            </div>
          </div>

          {/* Navigation */}
          <div className='fixed bottom-0 flex w-full max-w-4xl justify-between rounded-md border-t border-t-gray-200 bg-white px-4 py-3 shadow-md'>
            <button
              onClick={goToPrevious}
              disabled={currentQuestion === 1 || isSubmitting}
              className='flex items-center space-x-2 rounded-md border border-primary px-6 py-2 text-gray-500 transition-colors hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
            >
              <ChevronLeft className='h-4 w-4' />
              <span>Previous</span>
            </button>
            <button
              onClick={handleSubmitAnswer}
              disabled={!canStartAnswering || isSubmitting}
              className='rounded-lg bg-green-400 px-6 py-2 font-medium text-white transition-colors hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-50'
            >
              {isSubmitting ? 'Submitting...' : 'Submit Answer'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Setup View (original)
  return (
    <div className='h-full bg-gray-50 p-6'>
      <div className='mx-auto max-w-4xl'>
        {/* Header */}
        <button
          onClick={() => setCurrentView('home')}
          className='mb-5 flex items-center'
        >
          <ChevronLeft className='mr-1 h-4 w-4 text-gray-600' />
          <h1 className='text-lg font-semibold text-gray-800'>
            Interview Setup
          </h1>
        </button>

        {/* Programming Language Selection */}
        <div className='mb-6 rounded-lg border border-gray-200 bg-white p-6'>
          <h2 className='mb-4 text-lg font-medium text-gray-700'>
            Select Programming Language
          </h2>
          <div className='grid grid-cols-5 gap-4'>
            {languages.map(lang => (
              <button
                key={lang.id}
                onClick={() => setSelectedLanguage(lang.id)}
                className={`rounded-lg border-2 p-4 transition-all duration-200 ${
                  selectedLanguage === lang.id
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div
                  className={`h-12 w-12 rounded-full ${lang.color} mx-auto mb-2 flex items-center justify-center`}
                >
                  <span className='text-xl'>{lang.icon}</span>
                </div>
                <div className='text-sm font-medium text-gray-700'>
                  {lang.name}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Topics Selection */}
        <div className='mb-6 rounded-lg border border-gray-200 bg-white p-6'>
          <h2 className='mb-4 text-lg font-medium text-gray-700'>
            Select Topics
          </h2>
          <div className='grid grid-cols-2 gap-4'>
            {topics.map(topic => (
              <div
                key={topic.id}
                className='rounded-lg border border-gray-200 bg-white p-4'
              >
                <label className='flex cursor-pointer items-start'>
                  <input
                    type='checkbox'
                    checked={selectedTopics.includes(topic.id)}
                    onChange={() => handleTopicToggle(topic.id)}
                    className='mr-3 mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                  />
                  <div>
                    <div className='mb-1 text-sm font-medium text-gray-800'>
                      {topic.title}
                    </div>
                    <div className='text-xs text-gray-500'>
                      {topic.description}
                    </div>
                  </div>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Difficulty Level Selection */}
        <div className='mb-6 rounded-lg border border-gray-200 bg-white p-6'>
          <h2 className='mb-4 text-lg font-medium text-gray-700'>
            Select Difficulty Level
          </h2>
          <div className='grid grid-cols-3 gap-4'>
            {difficulties.map(difficulty => (
              <label key={difficulty.id} className='cursor-pointer'>
                <input
                  type='radio'
                  name='difficulty'
                  value={difficulty.id}
                  checked={selectedDifficulty === difficulty.id}
                  onChange={e => setSelectedDifficulty(e.target.value)}
                  className='sr-only'
                />
                <div
                  className={`rounded-lg border-2 p-4 transition-all duration-200 ${
                    selectedDifficulty === difficulty.id
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className='mb-2 flex items-center'>
                    <div
                      className={`mr-3 h-4 w-4 rounded-full border-2 ${
                        selectedDifficulty === difficulty.id
                          ? 'border-blue-400 bg-blue-400'
                          : 'border-gray-300'
                      }`}
                    >
                      {selectedDifficulty === difficulty.id && (
                        <div className='h-full w-full scale-50 rounded-full bg-white'></div>
                      )}
                    </div>
                    <span className={`text-sm font-medium ${difficulty.color}`}>
                      {difficulty.label}
                    </span>
                  </div>
                  <div className='ml-7 text-xs text-gray-500'>
                    {difficulty.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Start Interview Button */}
        <div className='flex justify-center'>
          <button
            disabled={disableStartInterview()}
            onClick={startInterview}
            className='rounded-lg bg-green-400 px-8 py-3 font-medium text-white transition-colors duration-200 hover:bg-green-500'
          >
            Start Interview
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiTest;
